import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Link, useMatch } from 'react-router-dom'

import { completeAnimatingIn, completeAnimatingOut, completePreloading, saveNavTheme } from '../../reducers/nav'
import { calcComponentState } from '../../fxs/component-utils'
import { fetchApiData } from '../../actions/api'
import { apiResourceEndpoint, buildRoutePath, routes } from '../../constants/routes'

import Breadcrumb from '../elements/breadcrumb'

import { createSelector } from '@reduxjs/toolkit'
import { clearMusicSource, setMusicSource } from '../../reducers/sound'
import { Helmet } from 'react-helmet'
import { throttle } from 'lodash'

import ClayvilleLocationMobile from './story-mobile'
import StoryIntroduction from './story-introduction'
import StorySection from './story-section'
import StoryRealWorld from './story-real-world'

import { ReactComponent as TopTear } from '../../assets/svgs/top-tear-collage-02.svg'
// Collage placeholders and KI shapes:
import { ReactComponent as BgSvgOne } from '../../assets/svgs/ki-shape-01.svg'
import { ReactComponent as BgSvgTwo } from '../../assets/svgs/ki-shape-02.svg'
import { ReactComponent as BgSvgThree } from '../../assets/svgs/ki-shape-03.svg'
import { ReactComponent as BgSvgFour } from '../../assets/svgs/ki-shape-04.svg'
import { ReactComponent as BgSvgFive } from '../../assets/svgs/ki-shape-05.svg'
// top edge of the collage section
import { ReactComponent as CollageBorder } from '../../assets/svgs/clayville-collage-border-01.svg'

const selectLocationStories = state => state.locationStories
const selectLocationSlug = (state, location) => location
const selectKeyIssueCategories = state => state.keyIssueCategories
const selectKeyIssues = state => state.keyIssues
const selectRelatedRealWorlds = state => state.realWorldStories
const selectStory = (state, storyObj) => storyObj

const withBaseUrl = (WrappedComponent) => {
    return function (props) {
        let match = useMatch(routes.clayvilleView.path)
        return <WrappedComponent params={match?.params} {...props} />
    }
}

const makeStory = createSelector(
    [selectLocationSlug, selectLocationStories],
    (locationSlug, stories) => {
        return stories.find(story => story.locationSlug === locationSlug)
    }
)

const makeLocationKeyIssues = createSelector(
    [selectKeyIssueCategories, selectStory],
    (keyIssueCategories, story) => {
        if (!story) {
            return null
        }
        return keyIssueCategories.find(ki => {
            return story.locationKeyIssue === ki.uuid
        })
    }
)

const makeRelatedIssues = createSelector(
    [selectKeyIssues, selectStory],
    (keyIssues, story) => {
        if (story?.relatedKeyIssueIds) {
            return keyIssues.filter(ki =>
                story.relatedKeyIssueIds.includes(ki.uuid)
            )
        }
        return []
    }
)

const makeRelatedRealWorlds = createSelector(
    [selectRelatedRealWorlds, selectStory],
    (realWorlds, story) => {
        if (story?.relatedRealStoryIds) {
            return realWorlds.filter(rw =>
                story.relatedRealStoryIds.includes(rw.uuid)
            )
        }
        return []
    }
)

const mapStateToProps = (state, ownProps) => {
    let story = makeStory(state, ownProps?.params?.location)
    return {
        navState: state.nav,
        locationKeyIssue: makeLocationKeyIssues(state, story),
        story: story,
        uiHeight: state.ui.height,
        uiDevice: state.ui.deviceType,
        relatedIssues: makeRelatedIssues(state, story),
        relatedRealWorlds: makeRelatedRealWorlds(state, story)
    }
}

const ClayvilleLocation = ({ dispatch, navState, params, story, uiHeight, uiDevice, locationKeyIssue, relatedIssues, relatedRealWorlds }) => {
    const [scrolledHalf, setScrolledHalf] = useState(false)
    const [scrolledSeventyFive, setScrolledSeventyFive] = useState(false)
    const [scrolledNinety, setScrolledNinety] = useState(false)
    const componentName = 'Clayville Location'
    const slideHeight = uiDevice === 'mobile' ? uiHeight * 1.5 : uiHeight * 5
    // const [menuOpen, setMenuOpen] = useState(true)
    const [totalScroll, setTotalScroll] = useState(0)
    const [activeSectionNum, setActiveSectionNum] = useState(-1)
    const color = locationKeyIssue?.color || 'blue'
    const [pendingScroll, setPendingScroll] = useState(0)
    const [normalScrollMode, setNormalScrollMode] = useState(false)
    const [wrapUpFullyRevealed, setWrapUpFullyRevealed] = useState(false)
    const wrapUpFullyRevealedRef = useRef(false)
    const pendingDeltaRef = useRef(0)
    const previousTouchY = useRef(-1)
    const storyWrapperRef = useRef(null)
    const wrapUpScrollRef = useRef(0)
    const collageElRef = useRef(null)

    const debouncedUpdateScroll = useRef(
        throttle(() => {
            setPendingScroll(pendingDeltaRef.current)
            previousTouchY.current = -1
        }, 25)
    ).current

    useEffect(() => {
        if (pendingScroll !== 0) {
            processAnimation()
        }
    }, [pendingScroll])

    const componentState = useMemo(() => {
        return calcComponentState(componentName, navState)
    }, [navState, componentName])

    useEffect(() => {
        if (componentState === 'PRELOADING') {
            // console.log('PRELOADING: story')
            handlePreloadingState()
        } else if (componentState === 'ANIMATING_IN') {
            // console.log('ANIMATING_IN: story')
            handleAnimatingInState()
        } else if (componentState === 'ANIMATING_OUT') {
            handleAnimatingOutState()
        }
    }, [componentState])

    const handlePreloadingState = async () => {
        dispatch(fetchApiData(apiResourceEndpoint(`locations/${params.location}`, 'LIST')))
            .then(() => {
                dispatch(completePreloading())
            })
    }

    const handleAnimatingInState = async () => {
        if (navState.theme !== 'white' && ['blue', 'magenta', 'teal'].includes(color)) {
            dispatch(saveNavTheme('white'))
        } else {
            // Otherwise make sure it is grey
            dispatch(saveNavTheme('grey'))
        }
        if (story.locationPrimaryAudio) {
            dispatch(setMusicSource(story.locationPrimaryAudio))
        } else {
            dispatch(clearMusicSource())
        }
        dispatch(completeAnimatingIn())
    }

    const handleAnimatingOutState = () => {
        // setActiveChapter(0)
        // setShowWrapUp(false)
        // setMenuOpen(true)
        setActiveSectionNum(-1)
        setTotalScroll(0)
        setNormalScrollMode(false)
        setWrapUpFullyRevealed(false)
        wrapUpScrollRef.current = 0
        if (storyWrapperRef.current) {
            storyWrapperRef.current.style.transform = ''
            storyWrapperRef.current.style.pointerEvents = ''
        }
        dispatch(completeAnimatingOut())
    }

    useEffect(() => {
        if (!story) {
            return
        }
        // let possibleScroll = Math.round((story.sections.length + 2) * slideHeight - (slideHeight / 2))
        let possibleScroll = Math.round((story.sections.length + 0.5) * slideHeight - (slideHeight / 2))
        let scrollPercentage = Math.round((totalScroll / possibleScroll) * 100)
        if (scrollPercentage > 100) {
            scrollPercentage = 100
        }
        if (scrollPercentage < 0) {
            scrollPercentage = 0
        }

        if (scrollPercentage >= 50 && !scrolledHalf) {
            setScrolledHalf(true)
            dispatchGtmScrollEvent(50)
        }
        if (scrollPercentage >= 75 && !scrolledSeventyFive) {
            setScrolledSeventyFive(true)
            dispatchGtmScrollEvent(75)
        }
        if (scrollPercentage >= 90 && !scrolledNinety) {
            setScrolledNinety(true)
            dispatchGtmScrollEvent(90)
        }
    }, [totalScroll, scrolledHalf, scrolledSeventyFive, scrolledNinety])

    const dispatchGtmScrollEvent = (value) => {
        let dataLayer = window.dataLayer || []
        dataLayer.push({
            event: 'scrollThreshold',
            scrollPercent: value
        })
    }

    useEffect(() => {
        wrapUpFullyRevealedRef.current = wrapUpFullyRevealed
    }, [normalScrollMode, wrapUpFullyRevealed])

    useEffect(() => {
        if (!normalScrollMode || !wrapUpFullyRevealed) { return }
        const onWheel = (evt) => {
            if (!collageElRef.current) { return }
            const el = collageElRef.current
            const delta = evt.deltaY
            if (el.scrollTop <= 0 && delta < 0) {
                setWrapUpFullyRevealed(false)
                wrapUpFullyRevealedRef.current = false
                applyWrapUpScroll(delta)
            } else {
                el.scrollTop += delta
            }
        }
        window.addEventListener('wheel', onWheel, { passive: true })
        return () => {
            window.removeEventListener('wheel', onWheel)
        }
    }, [normalScrollMode, wrapUpFullyRevealed])

    useEffect(() => {
        if (uiDevice === 'mobile' || uiDevice === 'tablet') { return }
        const isActive = componentState !== 'OFF' && componentState !== 'PRELOADING'
        const appEl = document.querySelector('#app')
        if (appEl) { appEl.style.overflowY = isActive ? 'hidden' : '' }
        return () => {
            const el = document.querySelector('#app')
            if (el) { el.style.overflowY = '' }
        }
    }, [componentState, uiDevice])

    const applyWrapUpScroll = (delta) => {
        const newScroll = Math.max(0, Math.min(uiHeight, wrapUpScrollRef.current + delta))
        wrapUpScrollRef.current = newScroll
        if (storyWrapperRef.current) {
            storyWrapperRef.current.style.transform = `translateY(-${newScroll}px)`
        }
        if (newScroll >= uiHeight) {
            if (storyWrapperRef.current) { storyWrapperRef.current.style.pointerEvents = 'none' }
            setWrapUpFullyRevealed(true)
        } else if (newScroll <= 0) {
            if (storyWrapperRef.current) {
                storyWrapperRef.current.style.transform = ''
                storyWrapperRef.current.style.pointerEvents = ''
            }
            setNormalScrollMode(false)
        }
    }

    const handleWrapUpRevealWheel = (evt) => {
        applyWrapUpScroll(parseFloat(evt.deltaY))
    }

    const handleWrapUpWheel = (evt) => {
        const delta = parseFloat(evt.deltaY)
        if (!wrapUpFullyRevealedRef.current) {
            applyWrapUpScroll(delta)
            return
        }
        if (collageElRef.current && collageElRef.current.scrollTop <= 0 && delta < 0) {
            setWrapUpFullyRevealed(false)
            wrapUpFullyRevealedRef.current = false
            applyWrapUpScroll(delta)
        }
    }

    if (componentState === 'OFF' || componentState === 'PRELOADING' || !story) {
        return null
    }

    const processAnimation = (tScroll = totalScroll) => {
        let newTotalScroll = tScroll + Math.round(parseFloat(pendingScroll))
        if (newTotalScroll < -1) {
            newTotalScroll = -1
            // setMenuOpen(true)
        // } else if (newTotalScroll > (story.sections.length + 2) * slideHeight - (slideHeight / 2)) {
        } else if (newTotalScroll > (story.sections.length + 0.5) * slideHeight - (slideHeight / 2)) {
            setNormalScrollMode(true)
            pendingDeltaRef.current = 0
            setPendingScroll(0)
            return null
        }
        const newPercentage = newTotalScroll / slideHeight * 100
        const relativePercentage = newPercentage % 100
        setTotalScroll(newTotalScroll)
        const newActiveSectionNum = Math.floor(newPercentage / 100)
        if (newActiveSectionNum !== activeSectionNum) {
            setActiveSectionNum(newActiveSectionNum)
        }

        let classes = [
            '.section-bg', '.section-text',
            '.section-image-first', '.section-image-second',
            '.real-world-stories'
        ]
        let targets = []
        classes.forEach(c => {
            let el = document.querySelector(`#section-${newActiveSectionNum} ${c}`)
            if (el) {
                targets.push(el)
            }
        })

        let percent = 1
        let opacity = 0
        let top = 0
        let scale = 1
        let tearHeight = 0
        if (relativePercentage < 30) {
            percent = relativePercentage / 30
            opacity = percent
            top = (1 - percent)
            scale = 1
            tearHeight = 10 + (60 * relativePercentage / 100)
        } else if (relativePercentage > 70) {
            percent = 1 - (relativePercentage - 70) / 30
            opacity = percent
            top = (percent - 1)
            scale = 1.2
            tearHeight = 70
        } else {
            opacity = 1
            top = 0
            tearHeight = 70
            // between 30 and 70, convert to 0 - .2 roughly
            let scaleRatio = ((relativePercentage - 30) / 40) / 10 * 2
            scale = 1 + scaleRatio
        }

        targets.forEach(target => {
            // build an anime sequence for each target
            // Scale the images during the pause
            if (target.className.includes('image')) {
                target.style.opacity = opacity
                target.style.transform = `scale(${scale})`
            }
            if (target.className.includes('world')) {
                target.style.height = `${tearHeight}vh`
            }
            // Scale the background during the whole scroll, prob need to randomize direction or switch on the index?
            if (target.className.includes('section-bg')) {
                target.style.transform = `scale(1.3) translateY(${-relativePercentage * 0.8}px)`
            }
            // Move the images before and after the pause
            if (target.className.includes('top') || target.className.includes('image-first')) {
                target.style.top = `${15 + (top * 100)}vh`
            } else if (target.className.includes('bottom') || target.className.includes('image-second')) {
                target.style.top = `${55 + (top * 50)}vh`
            } else if (target.className.includes('middle')) {
                target.style.top = `${35 + (top * 75)}vh`
            } else if (target.className === 'section-text') {
                target.style.opacity = opacity
                target.style.marginTop = `${0 + top * 50}px`
            }
        })

        // try to move the background pieces into place quickly
        let topDelta = 0
        let bgOpacity = 0.5
        let bgScale = 1
        let bgBlur = 0
        if (relativePercentage < 15) {
            // dividing by 6 so that we don't go all the way to the top
            topDelta = (relativePercentage / 15) * 100
            bgOpacity = 0.1 + (relativePercentage / 15) * 0.45
        } else if (relativePercentage >= 15 && relativePercentage <= 90) {
            bgScale = 1 + ((relativePercentage - 5) / 500)
            bgOpacity = 0.5
            topDelta = 100
        } else if (relativePercentage > 90 && relativePercentage < 100) {
            // short of 100 percent top
            bgScale = 1 + ((relativePercentage - 5) / 500)
            topDelta = ((relativePercentage - 90) / 10) * 100
            // blur on a scale of 0 to 100 based on the relative percentage
            // let t = (relativePercentage - 90) / 10
            bgBlur = (relativePercentage - 90) * 2
        } else {
            topDelta = 100
        }

        const topBg = document.querySelector(`#section-${newActiveSectionNum} .section-top-bg`)
        if (topBg) {
            if (relativePercentage <= 90 || relativePercentage >= 100) {
                topBg.style.top = `${-100 + topDelta}%`
            } else {
                topBg.style.top = `${-1 * topDelta}%`
            }
            topBg.style.transform = `scale(${bgScale})`
            topBg.style.opacity = bgOpacity
            topBg.style.filter = `blur(${bgBlur}px)`
        }
        const bottomBg = document.querySelector(`#section-${newActiveSectionNum} .section-bottom-bg`)
        if (bottomBg) {
            if (relativePercentage <= 90 || relativePercentage >= 100) {
                bottomBg.style.top = `${100 - topDelta}%`
            } else {
                bottomBg.style.top = `${-1 * topDelta}%`
            }
            bottomBg.style.transform = `scale(${bgScale})`
            bottomBg.style.opacity = bgOpacity
            bottomBg.style.filter = `blur(${bgBlur}px)`
        }

        let slideOpacity = 0
        // let slideTop = 0
        if (relativePercentage < 10) {
            slideOpacity = relativePercentage / 10
            // slideTop = 0
        } else if (relativePercentage > 90) {
            slideOpacity = 1 - ((relativePercentage - 90) / 10)
            // slideTop = -1 * ((relativePercentage - 90) / 10) * 100
        } else {
            // slideTop = 0
            slideOpacity = 1
        }
        const slideEl = document.querySelector(`#section-${newActiveSectionNum}`)
        if (slideEl) {
            document.querySelector(`#section-${newActiveSectionNum}`).style.opacity = slideOpacity
            // document.querySelector(`#section-${newActiveSectionNum}`).style.top = `${slideTop}%`
        }
        const nextSlideEl = document.querySelector(`#section-${newActiveSectionNum + 1}`)
        if (nextSlideEl) {
            nextSlideEl.style.opacity = 0
            // nextSlideEl.style.top = '0'
        }
        const prevSlideEl = document.querySelector(`#section-${newActiveSectionNum - 1}`)
        if (prevSlideEl) {
            prevSlideEl.style.opacity = 0
            // prevSlideEl.style.top = '-100%'
        }

        setPendingScroll(0)
        pendingDeltaRef.current = 0
    }

    const handleScroll = (evt) => {
        let deltaY = parseFloat(evt.deltaY)
        if (activeSectionNum === story.sections.length) {
            deltaY = deltaY * 2
        }
        pendingDeltaRef.current = pendingDeltaRef.current + deltaY
        debouncedUpdateScroll()
    }

    const handleTouch = (evt) => {
        let deltaY = 0
        const currentY = evt.touches[0].clientY
        if (previousTouchY.current === -1) {
            previousTouchY.current = currentY
        } else {
            deltaY = previousTouchY.current - currentY
            previousTouchY.current = currentY
            if (activeSectionNum === story.sections.length) {
                deltaY = deltaY * 2
            }
        }
        pendingDeltaRef.current = pendingDeltaRef.current + deltaY
        debouncedUpdateScroll()
    }

    const renderCollageHeader = () => {
        let headerParts = []
        let collageHeader = story.locationCollageHeader || ''
        let clayvilleLink = <Link to={routes.clayvilleIndex.path} target="_blank" rel="noopener noreferrer">Clayville landmarks</Link>
        let murphysLink = <Link to={routes.murphysIndex.path} target="_blank" rel="noopener noreferrer">Tales from the East Side</Link>
        if (collageHeader.includes('Clayville landmarks')) {
            let parts = collageHeader.split('Clayville landmarks')
            parts.forEach((part, index) => {
                headerParts.push(part)
                if (index < parts.length - 1) {
                    headerParts.push(clayvilleLink)
                }
            })
        }
        if (collageHeader.includes('Tales from the East Side')) {
            if (!headerParts.length) {
                let parts = collageHeader.split('Tales from the East Side')
                // headerParts = []
                parts.forEach((part, index) => {
                    headerParts.push(part)
                    if (index < parts.length - 1) {
                        headerParts.push(murphysLink)
                    }
                })
            } else {
                let newHeaderParts = []
                headerParts.forEach((part, index) => {
                    if (typeof part === 'string' && part.includes('Tales from the East Side')) {
                        let parts = part.split('Tales from the East Side')
                        parts.forEach((subPart, subIndex) => {
                            newHeaderParts.push(subPart)
                            if (subIndex < parts.length - 1) {
                                newHeaderParts.push(murphysLink)
                            }
                        })
                    } else {
                        newHeaderParts.push(part)
                    }
                })
                headerParts = newHeaderParts
            }
        }
        return (
            <div className="story-collage-header">
                <h2>{headerParts}</h2>
            </div>
        )
    }

    const renderCollageItems = () => {
        let allItems = []
        story.locationFooterCollage.forEach((item, idx) => {
            let data = item.data
            let type = item.type
            // If we have an SVG, use it, else default to the shape svgs based on the index for visual interest
            let SvgBackground = BgSvgOne // default
            if (idx === 1) {
                SvgBackground = BgSvgTwo
            } else if (idx === 2) {
                SvgBackground = BgSvgThree
            } else if (idx === 3) {
                SvgBackground = BgSvgFour
            } else if (idx === 4) {
                SvgBackground = BgSvgFive
            }
            let svg = <SvgBackground className={`collage-svg ${data.color ? data.color : ''}`} />
            if (data.svgShape) {
                svg = <img src={data.svgShape.url} alt={`${data.svgShape?.alt} shape`} className="collage-svg" />
            }
            let bg = null
            if (data.background) {
                svg = null
                bg = <img src={data.background.url} alt={`${data.background?.alt} background`} className="collage-bg" />
            }
            let mg = null
            if (data.middleground) {
                mg = <img src={data.middleground.url} alt={`${data.middleground?.alt} middleground`} className="collage-mg" />
            }
            let fg = null
            if (data.foreground) {
                fg = <img src={data.foreground.url} alt={`${data.foreground?.alt} foreground`} className="collage-fg" />
            }
            let itemRoute = null
            if (item.type === 'character') {
                itemRoute = buildRoutePath(routes.murphysView.path, { character: data.alias.substring(1) })
            } else if (item.type === 'location') {
                itemRoute = buildRoutePath(routes.clayvilleView.path, { location: data.alias.substring(1) })
            } else if (item.type === 'key_issue') {
                let cleanAlias = data.alias.replace(/\/key-issues\//, '')
                itemRoute = buildRoutePath(routes.keyIssueOverview.path, { keyIssueSlug: cleanAlias })
            }
            if (type === 'placeholder') {
                allItems.push(
                    <div key={`collage-item-${idx}`} className={`story-collage-item placeholder`}>
                        <div className="collage-item-header">Placeholder</div>
                        <div className="collage-item-images">
                            { svg }
                        </div>
                    </div>
                )
            } else {
                allItems.push(
                    <a key={`collage-item-${idx}`} className={`story-collage-item ${type}`} href={itemRoute} target="_blank" rel="noopener noreferrer">
                        <div className="collage-item-header">{ data.name }</div>
                        <div className="collage-item-images">
                            { svg }
                            { bg }
                            { mg }
                            { fg }
                        </div>
                    </a>
                )
            }
        })
        return allItems
    }

    const renderLocationSections = () => {
        let allSections = []
        story.sections.forEach((section, index) => {
            allSections.push(
                <StorySection
                    key={index}
                    index={index}
                    section={section}
                    color={color}
                    isMobile={false}
                    inActive={activeSectionNum !== index}
                />
            )
        })
        return allSections
    }

    if (uiDevice === 'mobile' || uiDevice === 'tablet') {
        return <ClayvilleLocationMobile />
    }

    // let possibleScroll = Math.round((story.sections.length + 2) * slideHeight - (slideHeight / 2))
    let possibleScroll = Math.round((story.sections.length + 0.5) * slideHeight - (slideHeight / 2))
    let scrollPercentage = Math.round((totalScroll / possibleScroll) * 100)
    if (scrollPercentage > 100) {
        scrollPercentage = 100
    }
    if (scrollPercentage < 0) {
        scrollPercentage = 0
    }

    // const wrapUpImage = story.locationWrapUpImage?.url || story.locationThumbnail?.url || null
    // const wrapUpImageAlt = story.locationWrapUpImage?.alt || story.locationThumbnail?.alt || null

    return (
        <>
            <Helmet>
                <title>{story.pageTitle}</title>
                <meta name="description" content={story.metaDescription} />
                <meta property="og:title" content={story.pageTitle} />
                <meta property="og:description" content={story.metaDescription} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:image" content={story.metaImage?.url} />
            </Helmet>
            <Breadcrumb>
                <Link to={routes.clayvilleIndex.path}>
                    Discover Clayville
                </Link> &bull; {story.locationName}
            </Breadcrumb>
            <div ref={storyWrapperRef} className={`clayville-story ${color}-bg`}>
                <div
                    className="location-wrapper"
                    onWheel={!normalScrollMode ? handleScroll : (!wrapUpFullyRevealed ? handleWrapUpRevealWheel : undefined)}
                    onTouchMove={!normalScrollMode ? handleTouch : undefined}
                >
                    <StoryIntroduction
                        index={-1}
                        color={color}
                        story={story}
                        isMobile={true}
                        inActive={activeSectionNum > -1}
                        onSetActiveSectionNum={!normalScrollMode ? setActiveSectionNum : undefined} />
                    {renderLocationSections()}
                </div>
                {!normalScrollMode && (
                    <div className="clayville-progress-wrapper">
                        <div className="clayville-progress" style={{ width: `${scrollPercentage}%` }} />
                    </div>
                )}
                <div className="real-world-tears top-tear-collage">
                    <TopTear />
                </div>
            </div>
            {normalScrollMode && (
                <div ref={collageElRef} className="location-wrap-up" style={{ overflowY: 'hidden' }} onWheel={handleWrapUpWheel}>
                    {/* Equivalent to the Character wrap up section ending in the character collage. */}
                    <div className="container">
                        <div className="location-wrap-up-header">
                            &nbsp;
                            {/* <div>
                                <h1>Making a Way Out of No Way</h1>
                                <MarkdownWrapper content={story.locationWrapUpDescription} />
                            </div>
                            <div className="location-image-wrapper">
                                <LocationSvg location={story} />
                                <img src={wrapUpImage} alt={wrapUpImageAlt} />
                            </div> */}
                        </div>
                        {/* Real World Stories */}
                        <StoryRealWorld
                            index={story.sections.length + 2}
                            relatedRealWorlds={relatedRealWorlds}
                            color={color}
                            story={story}
                            isMobile={false}
                            locationName={story.locationName}
                            onSetActiveSectionNum={!normalScrollMode ? setActiveSectionNum : undefined}
                        />
                        {/* StoryCollage will go here */}
                    </div>
                    <div className="story-collage-wrapper">
                        <div className="story-collage-border">
                            <CollageBorder />
                        </div>
                        <div className="container">
                            { renderCollageHeader() }
                        </div>
                        <div className="story-collage-items">
                            { renderCollageItems() }
                        </div>
                    </div>
                </div>
            )}
            {/* {renderClayvilleMenu()} */}
        </>
    )
}

export default withBaseUrl(connect(mapStateToProps)(ClayvilleLocation))
